import { useState, useEffect, useRef } from "react";
import { Stopwatch } from "./Stopwatch";

export type SessionState = "ready" | "recording" | "paused" | "stopped";

interface RecorderOutput {
  state: SessionState;
  analyser: AnalyserNode | null;
  // Value offset from performance.now() when recording started, or null if not currently recording
  getElapsedMs: () => number;
  recordingFile: File | null;
  audioQuality: { sampleRate: number | null; sampleSize: number | null } | null;
  start: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

function extFromMimeType(mimeType: string): string {
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("webm")) return "webm";
  throw new Error(`Unsupported mime type: ${mimeType}`);
}
 
export function useRecorder(): RecorderOutput {
  const [state, setState] = useState<SessionState>("ready");
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [audioQuality, setAudioQuality] = useState<{ sampleRate: number | null; sampleSize: number | null } | null>(null);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const stopwatchRef = useRef<Stopwatch>(new Stopwatch());
  const maxDurationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const MAX_RECORDING_MS = 4 * 60 * 60 * 1000; // 4 hours

  const teardown = () => {
    if (maxDurationTimerRef.current) {
      clearInterval(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    analyserNodeRef.current = null;
    recorderRef.current = null;
    stopwatchRef.current?.reset();
    setAnalyser(null);
  };

  const start = async () => {
    setRecordingFile(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const settings = stream.getAudioTracks()[0]?.getSettings();
      setAudioQuality({
        sampleRate: settings?.sampleRate ?? null,
        sampleSize: settings?.sampleSize ?? null,
      });

      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const node = ctx.createAnalyser();
      node.fftSize = 2048;
      ctx.createMediaStreamSource(stream).connect(node);
      analyserNodeRef.current = node;

      const opfsRoot = await navigator.storage.getDirectory();
      const fileHandle = await opfsRoot.getFileHandle(`${crypto.randomUUID()}.tmp`, { create: true });
      const writable = await fileHandle.createWritable();

      const recorder = new MediaRecorder(stream);
      
      recorder.ondataavailable = async (e) => {
        console.log('dataavailable', e.timeStamp)
        if (e.data.size > 0) await writable.write(e.data);
      };
      recorder.onstop = async () => {
        await writable.close();
        const file = await fileHandle.getFile();
        const blob = new Blob([await file.arrayBuffer()], { type: recorder.mimeType });
        await opfsRoot.removeEntry(fileHandle.name);
        const ext = extFromMimeType(recorder.mimeType);
        setRecordingFile(new File([blob], `recording.${ext}`, { type: recorder.mimeType }));
        teardown();
        setState("stopped");
      };
      recorderRef.current = recorder;
      recorder.start(5 * 60 * 1000); // Chunk data every 5 minutes
      maxDurationTimerRef.current = setInterval(() => {
        if (stopwatchRef.current.getElapsedMs() >= MAX_RECORDING_MS) stop();
      }, 10 * 1000);

      stopwatchRef.current?.start();
      setAnalyser(node);
      setState("recording");
    } catch {
      // Permission denied or no mic — stay in current state
    }
  };

  const pause = () => {
    recorderRef.current?.pause();
    recorderRef.current?.requestData();
    stopwatchRef.current?.stop();
    setAnalyser(null);
    setState("paused");
  };

  const resume = () => {
    recorderRef.current?.resume();
    stopwatchRef.current?.start();
    setAnalyser(analyserNodeRef.current);
    setState("recording");
  };

  const stop = () => {
    recorderRef.current?.stop(); // onstop handles teardown + setState
    stopwatchRef.current?.stop();
  };

  useEffect(() => {
    return () => {
      teardown();
    };
  }, []);

  return { state, analyser, getElapsedMs: () => stopwatchRef.current.getElapsedMs(), recordingFile, audioQuality, start, pause, resume, stop };
}
